import {
    GetServerSideProps, GetServerSidePropsContext, InferGetServerSidePropsType 
} from 'next';
import Head from 'next/head';
import {
    useRouter 
} from 'next/router';
import {
    memo, useCallback, useContext, useEffect, useMemo, useState 
} from 'react';
import SessionContext from '../../contexts/session';
import {
    activeFormId, redirectUserAfterLogin, refreshUserAndStoreIn 
} from '../../logic/auth';
import {
    createUserOrError, 
    preloadImages
} from '../../logic/diagnostic';
import {
    Data, get, getWebsiteDomain, post, put 
} from '../../networking';
import {
    ampIdentifySetMultiple, ampLogEvent 
} from '../../lib/amplitude';
import {
    useCookies 
} from 'react-cookie';
import Cookies from 'cookies';
import {
    NOT_LOADED_HINT 
} from '../_app';
import Loading from '../../components/core/loading';
import * as gtag from '../../lib/gtag';
import {
    postCheckoutInfo 
} from '../../logic/pricing';
import useAppUserAgent from '../../components/core/use-app-user-agent';
import Script from 'next/script';
import dataDogLogger from '../../lib/datadog';

import DiagnosticForm20240708 from '../../components/diagnostic/forms/signup/2024-07-08';
import DiagnosticForm2024090901 from '../../components/diagnostic/forms/signup/2024-09-09-01';
import DiagnosticForm2024090902 from '../../components/diagnostic/forms/signup/2024-09-09-02';
import DiagnosticForm2024090903 from '../../components/diagnostic/forms/signup/2024-09-09-03';
import DiagnosticForm2024090904 from '../../components/diagnostic/forms/signup/2024-09-09-04';
import DiagnosticForm20241022 from '../../components/diagnostic/forms/signup/2024-10-22';
import DiagnosticFormDryishJanuary2025 from '../../components/diagnostic/forms/signup/challengeOptIns/DryishJanuary2025';
import DiagnosticFormSoberishMomDryishJanuary2025 from '../../components/diagnostic/forms/signup/challengeOptIns/SoberishMom-DryishJanuary2025';
import DiagnosticFormUCBDryishJanuary2025 from '../../components/diagnostic/forms/signup/challengeOptIns/UCB-DryishJanuary2025';
import DiagnosticFormABCFitnessDryishJanuary2025 from '../../components/diagnostic/forms/signup/challengeOptIns/ABCFitness-DryishJanuary2025';
import DiagnosticFormSoberishMom from '../../components/diagnostic/forms/signup/challengeOptIns/SoberishMom';
import DiagnosticForm20250122 from '../../components/diagnostic/forms/signup/2025-01-22';
import DiagnosticForm20250220 from '../../components/diagnostic/forms/signup/2025-02-20';
import DiagnosticFormZumba from '../../components/diagnostic/forms/signup/challengeOptIns/Zumba';
import {
    ThemeProvider 
} from '@mui/material';
import theme from '../../figmaTheme';
import {
    DiagnosticAnswers,
} from '../../components/diagnostic/config/types';
import {
    brazeUserTrack 
} from '../../lib/braze';
import {
    GOAL_CHOICES 
} from '../../components/diagnostic/config/types';
import {
    defaultSignupSections,
    DiagnosticFormContext
} from '../../contexts/form';
import {
    convertRetrievedAnswers,
    createDiagnosticComplete,
    createEmailSkip, 
    createPhoneSkip,
    fireTrackingEvents,
    payloadFormatter
} from '../../components/diagnostic/config/utils';

async function putCurrentUser(payload: Data): Promise<Data> {
    return await put('users/current-user', payload);
}

async function getPartialAnswers(formId: string, context?: GetServerSidePropsContext): Promise<Data> {
    return await get('forms/partial-answers/' + formId, {}, context);
}

async function getCouponInfo(id?: string, context?: GetServerSidePropsContext): Promise<Data> {
    if (!id) {
        return;
    }
    return await get(`payments-gateway/stripe/coupon/${id}`, {}, context);
}

export const getServerSideProps: GetServerSideProps = async (context: GetServerSidePropsContext) => {
    const {
        slug 
    } = context.query;
    const cookies = new Cookies(context.req, context.res);
    const couponId = cookies.get('ss_discount') || context.query.d;
    const formId = slug[0];

    const [
        initialPartialAnswers,
        couponResponse,
    ] = await Promise.all([
        getPartialAnswers(formId, context), // TODO: need server side auth
        getCouponInfo(couponId, context)
    ]);

    return {
        props: {
            initialPartialAnswers,
            metaData: {
                robots: true 
            },
            couponResponse: couponResponse || {}
        }
    };
};

export default memo(function Diagnostic(
    {
        initialPartialAnswers 
    }: InferGetServerSidePropsType<typeof getServerSideProps>
): JSX.Element {
    const diagnosticFormContext = useContext(DiagnosticFormContext);
    const router = useRouter();
    const {
        slug, d, utm_content, utm_source, po 
    } = router.query;
    const [cookies, setCookies] = useCookies();
    const session = useContext(SessionContext);
    const [isLoading, setIsLoading] = useState(true);
    const [override, setOverride] = useState(null);
    const [isComplete, setIsComplete] = useState(false);
    const [preventHistoryOverride, setPreventHistoryOverride] = useState(false);

    const appUserAgent = useAppUserAgent();
    let createdUser = false;
    const isPremiumPath = po || !!cookies['po'];

    const [initialFormId, screenIndex] = slug as string[];

    const postCheckoutData = async (coupon) => {
        const payload = {
            coupons: coupon,
        };
        await postCheckoutInfo(payload);
    };

    const formId = useMemo(() => {
        if (override) {
            return override;
        }
        if (!initialFormId || initialFormId === 'forms' || initialFormId === 'typeform') {
            return activeFormId;
        }

        return initialFormId;
    }, [initialFormId, override]);

    useEffect(() => {
        if (!isLoading || !formId) return;

        if (session.user.id && isPremiumPath) {
            putCurrentUser({
                flowType: 'premium'
            });
        }
        // Redirect user correctly if they have completed diagnostic
        if (session.user.id && !session.user.diagnosticInFlight) {
            if ((session.user.formId)?.startsWith('ss_')) {
                setIsLoading(false);
                return;
            }
            redirectUserAfterLogin(session.user, router, undefined, session.user.formId);
            setIsLoading(false);
        } else if (session.user.id && session.user.diagnosticInFlight) {
            // Check for active diagnostic feature flags to force the flow
            const forcedFormId = Object.keys(cookies)?.filter(key => key.startsWith('diag_'))?.find(key => cookies[key] === 'true')?.replace('diag_', '');
            if (forcedFormId && formId !== forcedFormId && !override) {
                overrideFormId(forcedFormId);
            } else if (!formId?.startsWith('ss_')) {
                dataDogLogger(
                    'info',
                    'Redirecting user to typeform diagnostic flow',
                    {
                        userId: session.user.id,
                        rawData: {
                            formId,
                            sessionFormId: session.user.formId,
                            routerQuery: router.query
                        }
                    }
                );
                // user came from a typeform diagnostic signup
                fireEvents(formId);
                router.push({
                    pathname: `/typeform-diagnostic/${formId}`,
                    query: {
                        answerRef: 'intro',
                        formId 
                    }
                });
                return;
            } else {
                if (formId !== session.user.formId) {
                    // This means a user dropped off a previous diagnostic and is now
                    // coming back through a new link with a new formId
                    overrideFormId(formId);
                    return;
                }
                // joint information capture ss_20250220
                fireEvents(formId);
                setIsLoading(false);
            }
        } else {
            createUserObject().then(() => {
                if (cookies?.ss_discount ?? d) {
                    postCheckoutData(cookies.ss_discount ?? d);
                }
                gtag.gaObject({
                    event: 'optimize.activate' 
                });
                session.setUI({
                    hideNav: true,
                });
            });
        }
    }, [session.user, override, formId]);

    const createUserObject = async () => {
        if (!createdUser && !session.user.id && session.user.email !== NOT_LOADED_HINT) {
            await createUserOrError(formId, session, cookies, appUserAgent, isPremiumPath);
            createdUser = true;
        }
    };

    const overrideFormId = async (formId: string) => {
        await putCurrentUser({
            formId 
        });
        await refreshUserAndStoreIn(session);
        setOverride(formId);
        router.replace(`/diagnostic/${formId}/0`);
    };

    const fireEvents = async (formId: string) => {
        ampLogEvent(
            'Created User via direct navigation for',
            {
                ...(session.user.id && {
                    user_id: session.user.id 
                }),
                formId,
                answerRef: screenIndex
            },
            cookies
        );
        ampLogEvent(
            'diagnostic',
            {
                ...(session.user.id && {
                    user_id: session.user.id 
                }),
                diagnosticVersion: formId
            },
            cookies
        );
    };

    useEffect(() => {
        if (utm_content) {
            setCookies('utm_content', utm_content, {
                path: '/',
                domain: getWebsiteDomain()
            });
        }
        if (utm_source) {
            setCookies('utm_source', utm_source, {
                path: '/',
                domain: getWebsiteDomain()
            });
        }
        // Used to make a seamless transition if !user.diagnosticInFlight
        if (session.user.email === NOT_LOADED_HINT) {
            setIsLoading(true);
        }
        if (d) {
            // expires in one hour
            const expiresAt = new Date(new Date().getTime() + (60 * 60 * 1000));
            setCookies('ss_discount', d, {
                path: '/',
                expires: expiresAt,
                domain: getWebsiteDomain()
            });
        }
    }, []);

    const setScreenDimensionProps = () => {
        ampIdentifySetMultiple([
            {
                key: 'screenWidth',
                value: window.innerWidth.toString()
            },
            {
                key: 'screenHeight',
                value: window.innerHeight.toString()
            }
        ]);
    };

    useEffect(() => {
        if (typeof window === 'undefined' || !isLoading) return;
        setScreenDimensionProps();

        window.addEventListener('resize', setScreenDimensionProps);

        return () => {
            window.removeEventListener('resize', setScreenDimensionProps);
        };
    }, [isLoading, setScreenDimensionProps]);

    /* SHARED DIAGNOSTIC UTILS */

    const onClickCompleteDiagnostic = () => {
        if (isComplete) return;
        setIsComplete(true);
        completeDiagnostic();
    };

    const skippySkipsEmail = createEmailSkip(diagnosticFormContext, 14);
    const skippySkipsPhone = createPhoneSkip(diagnosticFormContext, 7);
    const completeDiagnostic = useCallback(createDiagnosticComplete(
        diagnosticFormContext,
        cookies,
        session.user
    ), [diagnosticFormContext, cookies, session.user]);

    useEffect(() => {
        diagnosticFormContext.setAnswers = setAnswers;
        diagnosticFormContext.setScreenDirection = setScreenDirection;
        diagnosticFormContext.goBack = goToPreviousScreen;
        diagnosticFormContext.goNext = goToNextScreen;
        diagnosticFormContext.fireTrackingEvents = fireTrackingEvents;
        diagnosticFormContext.setEndGoal = setEndGoal;
        diagnosticFormContext.setCustomTargets = setCustomTargets;
        diagnosticFormContext.setPronouns = setPronouns;
        diagnosticFormContext.startAboutYouSection = startAboutYouSection;
        diagnosticFormContext.completeDiagnostic = onClickCompleteDiagnostic;
        diagnosticFormContext.skippySkipsEmail = skippySkipsEmail;
        diagnosticFormContext.skippySkipsPhone = skippySkipsPhone;

        diagnosticFormContext.type = 'signup';
        diagnosticFormContext.formId = formId;
        diagnosticFormContext.screenDirection = 'left';
        diagnosticFormContext.sections = defaultSignupSections;
        diagnosticFormContext.screenIndex = parseInt(screenIndex) || 0;
        diagnosticFormContext.currentScreenName = 'intro';
        diagnosticFormContext.startTime = null;
        router.replace(`/diagnostic/${formId}/${screenIndex || 0}`, null, {
            shallow: true 
        });
    }, [formId]);

    useEffect(() => {
        preloadImages([
            '/img/sunnyside/advisors/andrew_tatarsky.png',
            '/img/sunnyside/advisors/kate_bonham.webp',
            '/img/sunnyside/diagnostic/text1.png',
            '/img/sunnyside/diagnostic/text2.png',
            '/img/sunnyside/diagnostic/text3.png',
            '/img/sunnyside/diagnostic/hannah.png',
            '/img/sunnyside/diagnostic/ash.png',
            '/img/sunnyside/diagnostic/cairo.png',
            '/img/sunnyside/diagnostic/community.png',
        ]);
    }, []);

    useEffect(() => {
        if (screenIndex && parseInt(screenIndex) === 0) {
            fireTrackingEvents(diagnosticFormContext, 'diagnostic', cookies);
        }
    }, [screenIndex]);

    useEffect(() => {
        const {
            slug 
        } = router.query;
        if (!slug || !diagnosticFormContext.screenIndex) return;

        const incomingScreenIndex = parseInt(slug[1]);
        if (incomingScreenIndex !== diagnosticFormContext.screenIndex) {
            setPreventHistoryOverride(true);
            diagnosticFormContext.screenIndex = incomingScreenIndex;
            if (incomingScreenIndex < diagnosticFormContext.screenIndex) {
                diagnosticFormContext.startTime = null;
                diagnosticFormContext.screenDirection = 'right';
            } else {
                diagnosticFormContext.startTime = new Date().getTime();
                diagnosticFormContext.screenDirection = 'left';
            }
            ampLogEvent(
                'signup: clicked browser back button',
                {
                    formId,
                    screen: diagnosticFormContext.currentScreenName
                },
                cookies
            );
        }
    }, [router.query]);

    useEffect(() => {
        const getAndSetAnswers = async () => {
            diagnosticFormContext.answers = convertRetrievedAnswers(await getPartialAnswers(formId));
        };

        if (!initialPartialAnswers) {
            getAndSetAnswers();
            return;
        }

        const newAnswers = convertRetrievedAnswers(initialPartialAnswers);
        diagnosticFormContext.answers = Object.assign({}, newAnswers, diagnosticFormContext.answers);
    }, [initialPartialAnswers]);

    const setAnswers = async (partialAnswers: any) => {
        const newAnswers = Object.assign({}, diagnosticFormContext.answers, partialAnswers);

        const partialAnswerKeys = Object.keys(partialAnswers);
        if (partialAnswerKeys.length === 1) {
            post('forms/partial-answer', payloadFormatter(partialAnswerKeys[0], partialAnswers[partialAnswerKeys[0]], formId));
        } else {
            for (let i = 0; i < partialAnswerKeys.length; i++) {
                const ref = partialAnswerKeys[i];
                let payload = payloadFormatter(ref, partialAnswers[ref], formId);
                if (ref === 'other_goals') {
                    payload = payloadFormatter(ref, partialAnswers.other_goals.filter(goal => goal !== partialAnswers.top_goal), formId);
                }
                setTimeout(() => {
                    post('forms/partial-answer', payload);
                }, (i + 1) * 1000);
            }
        }

        diagnosticFormContext.answers = newAnswers;

        if (Object.keys(partialAnswers)[0] === 'email') {
            const {
                your_name: first_name, top_goal, other_goals 
            } = diagnosticFormContext.answers as DiagnosticAnswers;

            const diag_top_goal = GOAL_CHOICES.find(({
                key 
            }) => key === top_goal)?.legacyTitle;
            const diag_other_goals = (other_goals || []).map(goal => {
                return GOAL_CHOICES.find(({
                    key 
                }) => key === goal)?.legacyTitle;
            }).join(', ');

            brazeUserTrack({
                events: [{
                    name: 'submitted_email',
                    time: new Date().toISOString()
                }],
                attributes: [
                    {
                        diag_form_id: formId,
                        first_name,
                        diag_top_goal,
                        diag_other_goals
                    }
                ]
            });
        }
    };

    const setScreenDirection = (direction: 'left' | 'right') => {
        diagnosticFormContext.screenDirection = direction;
    };

    const changeUrl = () => {
        if (diagnosticFormContext.screenIndex === undefined || diagnosticFormContext.screenIndex === null) return;

        if (!preventHistoryOverride) {
            router.push(`/diagnostic/${formId}/${diagnosticFormContext.screenIndex}`, null, {
                shallow: true 
            });
        } else {
            router.replace(`/diagnostic/${formId}/${diagnosticFormContext.screenIndex}`, null, {
                shallow: true 
            });
        }
        setPreventHistoryOverride(false);
    };

    const goToPreviousScreen = (jump = 1) => {
        if (diagnosticFormContext?.currentScreenName) {
            ampLogEvent(
                'signup: clicked UI back button',
                {
                    formId,
                    screen: diagnosticFormContext?.currentScreenName
                },
                cookies
            );
        }
        diagnosticFormContext.setScreenDirection('right');
        diagnosticFormContext.screenIndex = diagnosticFormContext.screenIndex - jump;
        diagnosticFormContext.startTime = null;
        changeUrl();
    };

    const goToNextScreen = (jump = 1) => {
        diagnosticFormContext.startTime = new Date().getTime();
        diagnosticFormContext.setScreenDirection('left');
        diagnosticFormContext.screenIndex = diagnosticFormContext.screenIndex + jump;
        changeUrl();
    };

    const setEndGoal = async (partialAnswers: any) => {
        brazeUserTrack({
            attributes: [
                {
                    diag_end_goal: partialAnswers.endGoal
                }
            ]
        });
        if (partialAnswers.endGoal !== 'Drink Less') {
            const newPartialAnswers = Object.assign({}, partialAnswers, {
                customTargets: 'false' 
            });
            setAnswers(newPartialAnswers);
        } else {
            setAnswers(partialAnswers);
        }
    };

    const setCustomTargets = (partialAnswers: any) => {
        brazeUserTrack({
            attributes: [
                {
                    diag_custom_targets: partialAnswers.customTargets
                }
            ]
        });
        diagnosticFormContext.setAnswers(partialAnswers);
    };

    const setPronouns = (partialAnswers: any) => {
        brazeUserTrack({
            attributes: [
                {
                    diag_pronouns: partialAnswers.pronouns
                }
            ]
        });
        diagnosticFormContext.setAnswers(partialAnswers);
    };

    const startAboutYouSection = async () => {
        diagnosticFormContext.setAnswers(Object.assign({}, diagnosticFormContext.answers, convertRetrievedAnswers(await getPartialAnswers(formId))));
        diagnosticFormContext.goNext();
    };

    /* END SHARED DIAGNOSTIC UTILS */

    if (isLoading) {
        return <Loading />;
    }

    return (
        <ThemeProvider theme={theme}>
            <DiagnosticFormContext.Provider value={diagnosticFormContext}>
                <Head>
                    <title>
                        Sunnyside Diagnostic
                    </title>
                    <meta
                        name="viewport"
                        content="width=device-width, initial-scale=1, maximum-scale=1"
                    />
                    <meta
                        name="format-detection"
                        content="telephone=no"
                    />
                </Head>
                <Script></Script>
                {formId === 'ss_20240708' && (
                    <DiagnosticForm20240708
                        index={parseInt(screenIndex) || 0}
                    />
                )}
                {formId === 'ss_20240909-1' && (
                    <DiagnosticForm2024090901
                        index={parseInt(screenIndex) || 0}
                    />
                )}
                {formId === 'ss_20240909-2' && (
                    <DiagnosticForm2024090902
                        index={parseInt(screenIndex) || 0}
                    />
                )}
                {formId === 'ss_20240909-3' && (
                    <DiagnosticForm2024090903
                        index={parseInt(screenIndex) || 0}
                    />
                )}
                {formId === 'ss_20240909-4' && (
                    <DiagnosticForm2024090904
                        index={parseInt(screenIndex) || 0}
                    />
                )}
                {formId === 'ss_20241022' && (
                    <DiagnosticForm20241022
                        index={parseInt(screenIndex) || 0}
                    />
                )}
                {formId === 'ss_DryishJanuary2025' && (
                    <DiagnosticFormDryishJanuary2025
                        index={parseInt(screenIndex) || 0}
                    />
                )}
                {formId === 'ss_SoberishMomDryishJanuary2025' && (
                    <DiagnosticFormSoberishMomDryishJanuary2025
                        index={parseInt(screenIndex) || 0}
                    />
                )}
                {formId === 'ss_UCBDryishJanuary2025' && (
                    <DiagnosticFormUCBDryishJanuary2025
                        index={parseInt(screenIndex) || 0}
                    />
                )}
                {formId === 'ss_ABCFitnessDryishJanuary2025' && (
                    <DiagnosticFormABCFitnessDryishJanuary2025
                        index={parseInt(screenIndex) || 0}
                    />
                )}
                {formId === 'ss_soberishmom' && (
                    <DiagnosticFormSoberishMom
                        index={parseInt(screenIndex) || 0}
                    />
                )}
                {formId === 'ss_20250122' && (
                    <DiagnosticForm20250122
                        index={parseInt(screenIndex) || 0}
                    />
                )}
                {formId === 'ss_20250220' && (
                    <DiagnosticForm20250220
                        index={parseInt(screenIndex) || 0}
                    />
                )}
                {formId === 'ss_Zumba' && (
                    <DiagnosticFormZumba
                        index={parseInt(screenIndex) || 0}
                    />
                )}
            </DiagnosticFormContext.Provider>
        </ThemeProvider>
    );
});
